// Versions of an IFC parsing setup (docs/ifcps-save-analysis-proposal.md).
// One setup row is one version; the rows of a setup share `family`. The
// backend owns the rules (woodwall/companies/ps/versions.py); this file only
// groups, compares and counts for the screens. Pure, so it is unit tested;
// the layout request lives in `ifcps_layout.js`.

// The value without empty lists, objects and strings. The editors fill in
// missing fields and empty lists as they mount, which is not a change; the
// backend hashes the same normalised value.
export function normalised(value) {
    if (Array.isArray(value)) {
        const out = value.map(normalised).filter((x) => x !== undefined)
        return out.length ? out : undefined
    }
    if (value && typeof value == "object") {
        const out = {}
        Object.keys(value).sort().forEach((k) => {
            const v = normalised(value[k])
            if (v !== undefined) out[k] = v
        })
        return Object.keys(out).length ? out : undefined
    }
    if (value === "" || value === null || value === undefined) return undefined
    return value
}

const same = (a, b) => JSON.stringify(normalised(a)) == JSON.stringify(normalised(b))

function blockValue(form, block) {
    const field = form?.[block.field]
    if (["branches", "classes", "levels", "assembly_types", "module_types"].includes(block.kind)) return field
    return field?.[block.key]
}

// The sections whose content differs between two setups, in layout order,
// each with its effect. Used by the publish dialog and the move dialog.
export function changedSections(before, after, layout) {
    const out = []
    for (const tab of layout?.tabs || []) {
        for (const section of tab.sections) {
            if (section.blocks.some((b) => !same(blockValue(before, b), blockValue(after, b)))) {
                out.push({ ...section, tab_key: tab.key })
            }
        }
    }
    return out
}

// The setups of a company grouped by family, newest version first.
export function ifcpsFamilies(list) {
    const by_family = {}
    for (const ps of list || []) {
        const key = ps.family || ps.uuid
        if (!by_family[key]) by_family[key] = []
        by_family[key].push(ps)
    }
    return Object.entries(by_family).map(([family, versions]) => {
        versions.sort((a, b) => (b.version || 1) - (a.version || 1))
        return { family, newest: versions[0], versions }
    })
}

export function familyOf(list, ps) {
    if (!ps) return null
    return ifcpsFamilies(list).find((f) => f.family == (ps.family || ps.uuid)) || null
}

// Measured on production: an analysis takes 22 s on average, and a company
// runs up to 20 at once. Rounded up to whole minutes.
export function estimateMinutes(designs) {
    if (!designs) return 0
    return Math.max(1, Math.ceil((designs * 22) / 20 / 60))
}

// The version a design was analysed with, when it is not its project's
// current one. `null` when the design is current or was never stamped.
export function outdatedVersion(design, current_ps) {
    const stamp = design?.meta?.analyzed_ifcps
    if (!stamp || !current_ps?.analysis_fingerprint) return null
    if (stamp.fingerprint == current_ps.analysis_fingerprint) return null
    return stamp.version || null
}
